import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "../LanguageContext";
import { LOGO } from "../content";
import { scrollToId } from "../scroll";

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const onMenuPage = pathname === "/menu";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (pathname !== "/") navigate("/", { state: { scrollTo: id } });
    else scrollToId(id);
  };

  const links = [
    { id: "home", label: t.nav.home, action: () => goSection("home") },
    { id: "menu", label: t.nav.menu, action: () => goSection("featured") },
    { id: "gallery", label: t.nav.gallery, action: () => goSection("gallery") },
    { id: "reserve", label: t.nav.reserve, action: () => goSection("reserve") },
    { id: "contact", label: t.nav.contact, action: () => goSection("contact") },
  ];

  const dark = onMenuPage || scrolled; // dark text on light bg

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: onMenuPage ? 0.1 : 1.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        dark
          ? "bg-ivory/85 backdrop-blur-xl border-b border-sand/60 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
        <button
          onClick={() => goSection("home")}
          data-testid="logo-button"
          aria-label="Restaurante São João — home"
          className={`flex items-center transition-[background-color,padding] duration-500 rounded-full ${dark ? "bg-transparent" : "bg-ivory/90 px-3 py-1.5 shadow-sm"}`}
        >
          <img src={LOGO} alt="Restaurante São João logo" className="h-10 md:h-12 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={l.action}
              data-testid={`nav-${l.id}`}
              className={`text-sm tracking-wide transition-colors duration-300 hover:text-gold ${dark ? "text-brown/80" : "text-ivory/90"}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className={`text-xs tracking-widest font-medium border rounded-full px-3 py-1.5 transition-colors duration-300 ${
              dark ? "border-brown/25 text-brown hover:bg-brown hover:text-ivory" : "border-ivory/40 text-ivory hover:bg-ivory hover:text-brown"
            }`}
          >
            {lang === "en" ? "PT" : "EN"}
          </button>

          <button
            onClick={() => goSection("reserve")}
            data-testid="nav-reserve-button"
            className="hidden sm:inline-flex items-center bg-olive text-ivory text-sm tracking-wide rounded-full px-5 py-2.5 transition-[background-color,transform] duration-300 hover:bg-brown hover:-translate-y-0.5"
          >
            {t.nav.reserveBtn}
          </button>

          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className={`lg:hidden transition-colors duration-300 ${dark ? "text-brown" : "text-ivory"}`}
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-ivory/95 backdrop-blur-xl border-t border-sand/60 mt-3"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <button key={l.id} onClick={l.action} className="text-left font-serif text-2xl text-brown py-2 transition-colors hover:text-gold">
                  {l.label}
                </button>
              ))}
              <button onClick={() => goSection("reserve")} className="mt-4 bg-olive text-ivory rounded-full px-5 py-3 text-sm tracking-wide transition-colors hover:bg-brown">
                {t.nav.reserveBtn}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
